import React, { useMemo } from "react";
import ReactEcharts from "echarts-for-react";

const linearRegression = (points) => {
  const n = points.length;
  if (n < 2) return null;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (const [x, y] of points) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
};

export const StcScatterChart = ({ readings }) => {
  const { allPoints, fitLine, fit } = useMemo(() => {
    if (!readings) return { allPoints: [], fitLine: [], fit: null };

    const all = readings.map((r) => [r.flow, r.pressure_1 - r.pressure_2]);
    const filtered = all.filter(([flow]) => flow > 0.1);
    const reg = linearRegression(filtered);

    if (!reg) return { allPoints: all, fitLine: [], fit: null };

    const xVals = filtered.map(([x]) => x);
    const xMin = Math.min(...xVals);
    const xMax = Math.max(...xVals);
    const line = [
      [xMin, reg.slope * xMin + reg.intercept],
      [xMax, reg.slope * xMax + reg.intercept],
    ];

    return { allPoints: all, fitLine: line, fit: reg };
  }, [readings]);

  const option = {
    title: {
      text: `Pressure drop${fit ? `  (gradient: ${fit.slope.toFixed(3)})` : ""}`,
      textStyle: { color: "#ccc", fontSize: 13, fontWeight: "normal" },
      left: "center",
      top: 4,
    },
    grid: { left: 50, right: 20, top: 40, bottom: 55 },
    xAxis: {
      name: "Flow",
      type: "value",
      nameTextStyle: { color: "#ccc" },
      axisLabel: { color: "#ccc" },
    },
    yAxis: {
      name: "Pressure drop",
      type: "value",
      nameTextStyle: { color: "#ccc" },
      axisLabel: { color: "#ccc" },
    },
    tooltip: {
      trigger: "item",
      confine: true,
    },
    legend: {
      data: ["All readings", "Best fit (flow>0.1)"],
      textStyle: { color: "#ccc", fontSize: 10 },
      bottom: 0,
    },
    series: [
      {
        name: "All readings",
        data: allPoints,
        type: "scatter",
        symbolSize: 4,
      },
      {
        name: "Best fit (flow>0.1)",
        data: fitLine,
        type: "line",
        symbol: "none",
        lineStyle: { color: "#ff6644", width: 2, type: "dashed" },
        itemStyle: { color: "#ff6644" },
        tooltip: {
          formatter: () =>
            fit
              ? `y = ${fit.slope.toFixed(4)}x + ${fit.intercept.toFixed(4)}`
              : "",
        },
      },
    ],
  };

  return (
    <ReactEcharts option={option} style={{ height: 300, width: "100%" }} />
  );
};
